import { useQuery } from '@tanstack/react-query'
import { locationsApi } from '@/api/endpoints'

/**
 * State, then City — the cities offered are the chosen state's.
 *
 * Works in names, because that is what addresses store (`customers.state`,
 * `customers.city`); the lists come from Settings › States & cities. Changing
 * the state clears the city, since a city from another state cannot stay.
 *
 * A saved value no longer in the lists — typed before the lists existed, or
 * renamed since — is kept and shown as such, never silently dropped.
 */
export default function StateCitySelect({ state, city, onChange, disabled = false }) {
  const states = useQuery({
    queryKey: ['states'],
    queryFn: locationsApi.states,
    staleTime: 5 * 60_000,
  })

  const chosen = states.data?.find((s) => s.name.toLowerCase() === String(state ?? '').toLowerCase())

  const cities = useQuery({
    queryKey: ['cities', chosen?.id],
    queryFn: () => locationsApi.cities(chosen.id),
    enabled: Boolean(chosen),
    staleTime: 5 * 60_000,
  })

  const stateKnown = !state || Boolean(chosen)
  const cityKnown =
    !city || (cities.data ?? []).some((c) => c.name.toLowerCase() === String(city).toLowerCase())

  return (
    <>
      <label className="field">
        <span>State</span>
        <select
          value={chosen?.name ?? state ?? ''}
          disabled={disabled || states.isLoading}
          onChange={(e) => onChange({ state: e.target.value, city: '' })}
        >
          <option value="">{states.isLoading ? 'Loading…' : 'Select a state'}</option>
          {!stateKnown && <option value={state}>{state} (not in the list)</option>}
          {(states.data ?? []).map((s) => (
            <option key={s.id} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </label>

      <label className="field">
        <span>City</span>
        <select
          value={city ?? ''}
          disabled={disabled || !state || (chosen && cities.isLoading)}
          onChange={(e) => onChange({ state, city: e.target.value })}
        >
          <option value="">
            {!state
              ? 'Select a state first'
              : chosen && cities.isLoading
                ? 'Loading…'
                : chosen && cities.data?.length === 0
                  ? 'No cities in this state yet'
                  : 'Select a city'}
          </option>
          {city && (!chosen || (!cities.isLoading && !cityKnown)) && (
            <option value={city}>{city} (not in the list)</option>
          )}
          {(cities.data ?? []).map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
    </>
  )
}
